/**
 * Facebook Marketplace via the Mac agent.
 *
 * Listings are no longer posted by replaying Facebook's GraphQL API with
 * captured cookies. Instead the web app enqueues jobs on the shared mirror
 * queue with marketplace: "facebook"; a Mac running the agent in
 * tools/offerup-agent claims them and drives the "Seller" iOS app through
 * iPhone Mirroring. This module is the thin queue client.
 */
import api from "./api";
import { roundPrice } from "./offerup";

export { roundPrice };

/**
 * The old cookie flow is gone; the agent authenticates with its own
 * FrugalStack login. Pages that used to gate on configuration can always
 * offer Facebook actions now.
 */
export function isConfigured() {
  return true;
}

async function enqueue(action, entityId, payload) {
  const response = await api.post("/offerup-queue", {
    entity_id: entityId,
    action,
    payload,
    marketplace: "facebook",
  });
  return response.data?.data;
}

/**
 * Queue a listing for creation.
 * @param {Object} product - entity row (id, name, description, condition, prices)
 * @param {Object} options - { title, description, price, condition, category } overrides
 */
export function queueCreate(product, options = {}) {
  return enqueue("create", product.id, {
    title: options.title || product.name,
    description: options.description || product.description || product.name,
    price:
      options.price ??
      roundPrice(product.list_sale_price || product.min_sale_price || product.retail_price),
    condition: options.condition || product.condition || "good",
    category: options.category,
  });
}

/**
 * Facebook Marketplace's category list (from the Seller app's category
 * picker). The agent types the value into the picker's search box, so any
 * more specific category text works too.
 */
export const CATEGORIES = [
  "Antiques & Collectibles",
  "Appliances",
  "Arts & Crafts",
  "Auto Parts",
  "Baby",
  "Bags & Luggage",
  "Bicycles",
  "Books, Movies & Music",
  "Electronics & Computers",
  "Furniture",
  "Garden & Outdoor",
  "Health & Beauty",
  "Home Goods",
  "Home Improvement & Tools",
  "Jewelry & Accessories",
  "Kids' Clothing & Shoes",
  "Men's Clothing & Shoes",
  "Miscellaneous",
  "Mobile Phones",
  "Musical Instruments",
  "Patio & Garden",
  "Pet Supplies",
  "Sporting Goods",
  "Toys & Games",
  "Video Games & Consoles",
  "Women's Clothing & Shoes",
];

/** Queue an update for an already-posted listing. */
export function queueUpdate(product, options = {}) {
  return enqueue("update", product.id, {
    title: options.title || product.name,
    description: options.description,
    price: options.price,
    condition: options.condition,
    category: options.category,
    external_id: options.externalId,
    // The agent finds the listing by its currently-posted title, which can
    // differ from the new title when the update renames it.
    find_title: options.findTitle || options.title || product.name,
  });
}

/** Queue removal of a posted listing. */
export function queueDelete(product, options = {}) {
  return enqueue("delete", product.id, {
    title: options.title || product.name,
    external_id: options.externalId,
    // The agent finds the listing by its currently-posted title.
    find_title: options.findTitle || options.title || product.name,
  });
}

/** Recent Facebook jobs, optionally filtered by status. */
export async function listJobs(status) {
  const response = await api.get("/offerup-queue", {
    params: { marketplace: "facebook", ...(status ? { status } : {}) },
  });
  return response.data?.data || [];
}

/** Cancel a still-queued job. */
export async function cancelJob(jobId) {
  const response = await api.delete(`/offerup-queue/${jobId}`);
  return response.data?.data;
}

const facebook = {
  CATEGORIES,
  roundPrice,
  isConfigured,
  queueCreate,
  queueUpdate,
  queueDelete,
  listJobs,
  cancelJob,
};

export default facebook;
